import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaArrowLeft, FaSyncAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../services/api';
import logo from "../../assets/ojoto_union_logo.png";

const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timer, setTimer] = useState(0);

    useEffect(() => {
        // Get email from location state or fallback to prompt
        const stateEmail = location.state?.email;
        if (stateEmail) {
            setEmail(stateEmail);
        } else {
            // If no email in state, check if we can get it from somewhere else or redirect
            // For now, if no email, user might have come here directly, let them enter it
        }
    }, [location]);

    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;

        setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

        // Focus next input
        if (element.nextSibling && element.value !== '') {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            if (otp[index] === '' && e.target.previousSibling) {
                e.target.previousSibling.focus();
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const otpCode = otp.join('');

        if (otpCode.length !== 6) {
            toast.error('Please enter the full 6-digit code');
            return;
        }

        if (!email) {
            toast.error('Email address is missing');
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.auth.verifyOTP({ email, otp: otpCode });
            if (response.success) {
                toast.success('Email verified successfully! You can now log in.');
                navigate('/login');
            }
        } catch (error) {
            console.error('OTP Verification error:', error);
            toast.error(error?.message || 'Invalid or expired code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return;

        setIsResending(true);
        try {
            await api.auth.sendOTP({ email });
            toast.success('A new verification code has been sent to your email.');
            setTimer(60); // 1 minute cooldown
        } catch (error) {
            toast.error(error?.message || 'Failed to resend code');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Link
                to="/login"
                className="absolute top-6 left-6 flex items-center gap-2 text-gray-700 hover:text-primary-600 transition-colors"
            >
                <FaArrowLeft />
                <span>Back to Login</span>
            </Link>

            <div className="w-full max-w-md">
                <div className="card shadow-xl border-t-4 border-primary-600">
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-4">
                            <img
                                src={logo}
                                alt="Ojoto Union Logo"
                                className="h-16 w-16 object-cover"
                            />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">Verify Your Email</h1>
                        <p className="text-gray-600">
                            We've sent a 6-digit verification code to <br />
                            <span className="font-semibold text-primary-700">{email || 'your email'}</span>
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="flex justify-between gap-2 px-2">
                            {otp.map((data, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={data}
                                    onChange={(e) => handleOtpChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all outline-none border-gray-200"
                                />
                            ))}
                        </div>

                        <div className="space-y-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full btn-primary py-4 flex items-center justify-center gap-2 group"
                            >
                                {isLoading ? (
                                    <FaSyncAlt className="animate-spin" />
                                ) : (
                                    <>
                                        <FaLock className="group-hover:scale-110 transition-transform" />
                                        Verify Email
                                    </>
                                )}
                            </button>

                            <div className="text-center">
                                <p className="text-sm text-gray-600">
                                    Didn't receive the code?{' '}
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={isResending || timer > 0}
                                        className={`font-semibold transition-colors ${isResending || timer > 0
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-primary-600 hover:text-primary-700'
                                            }`}
                                    >
                                        {timer > 0 ? `Resend in ${timer}s` : 'Resend Code'}
                                    </button>
                                </p>
                            </div>
                        </div>
                    </form>

                    <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                        <p className="text-xs text-gray-400 max-w-xs mx-auto">
                            By verifying your email, you help us ensure the security of the Ojoto Union community.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
